import { DateTime } from "luxon";
import { ApiError, ApiErrorType } from "../Shared/sharedTypes";
import { ExternalObserverSession } from "./Auto/Interfaces/ExternalObserverSession";
import { Session, SessionInfo } from "./GeneratedTypes";

export async function handleResponseVoid(response: Response) {
    if (response.ok) return;
    if (response.status === 400) {
        // So, a server-side validation error occurred.
        // Server side validation returns a string error message, so parse as text instead of json.
        const error = await response.text();
        throw new Error(error);
    }
    throw new Error("Network response was not ok.");
}

export async function handleResponseJson(response: Response) {
    if (response.ok) return response.json();

    var responseErrorType = ApiErrorType.Unexpected;
    var internalErrorMessage = "Network response was not ok.";

    if (response.status === 400) {
        responseErrorType = ApiErrorType.BadRequest;
    } else if (response.status === 401) {
        responseErrorType = ApiErrorType.Unauthorized;
    } else if (response.status === 404) {
        responseErrorType = ApiErrorType.NotFound;
    }

    if (responseErrorType !== ApiErrorType.Unexpected) {
        internalErrorMessage = await response.text();
    }

    // eslint-disable-next-line
    throw {
        internalErrorMessage: internalErrorMessage,
        isOccurring: true,
        errorType: responseErrorType,
    } as ApiError;
}

//TODO: add Logging to AppInsights
export function handleError(error: Error) {
    // eslint-disable-next-line no-console
    console.error("API call failed. " + error);
    throw error;
}

export function assureSessionJsonStructure(session: Session, mentorCultureCode: string | null): Session {
    var sessionCultureCode = session.order.cultureCode;
    var backupCultureCode = "en-US";
    mentorCultureCode = mentorCultureCode || sessionCultureCode;

    session.order.customer.labels = session.order.customer.labels || [];
    session.order.tags = session.order.tags || [];
    session.order.tags.forEach((t) => (t.labelIds = t.labelIds || []));
    session.order.skipUDPConnections = session.order.skipUDPConnections || false;

    session.order.scheduledDate = session.order.scheduledDate
        ? DateTime.fromISO(session.order.scheduledDate as string)
        : undefined;

    session.order.expirationDate = session.order.expirationDate
        ? DateTime.fromISO(session.order.expirationDate as string)
        : undefined;

    session.order.mentorActiveSince = session.order.mentorActiveSince
        ? DateTime.fromISO(session.order.mentorActiveSince as string)
        : undefined;

    //labels only used on mentor side now.  So can try theirs first.
    session.order.customer.labels.forEach(
        (i) =>
            (i.currentLanguage =
                i.languages[mentorCultureCode!] ||
                i.languages[sessionCultureCode] ||
                i.languages[backupCultureCode] ||
                {})
    );

    //We want the tags themselves to always apply in language of Session. We could display to mentor in list in theirs
    //and apply to image in sessions
    session.order.tags.forEach(
        (i) => (i.currentLanguage = i.languages[sessionCultureCode] || i.languages[backupCultureCode] || {})
    );
    var product = session.order.product;
    product.currentLanguage =
        product.languages[mentorCultureCode] ||
        product.languages[sessionCultureCode] ||
        product.languages[backupCultureCode] ||
        {};

    session.sessionInfo = hydrateSessionInfo(session.sessionInfo);

    session.localizations = session.localizations || {
        id: session.order.id,
        localizations: {},
    };

    session.sessionInfo.recordingToggles = session.sessionInfo.recordingToggles || [];

    return session;
}


export function assureExternalObserverSessionJsonStructure(session: ExternalObserverSession, observerCultureCode: string | null): ExternalObserverSession {
    var sessionCultureCode = session.order.cultureCode;
    var backupCultureCode = "en-US";
    observerCultureCode = observerCultureCode || sessionCultureCode;

    session.order.cultureCode = observerCultureCode;

    session.order.customer.labels = session.order.customer.labels || [];
    session.order.tags = session.order.tags || [];
    session.order.tags.forEach((t) => (t.labelIds = t.labelIds || []));
    session.order.skipUDPConnections = session.order.skipUDPConnections || false;

    session.order.scheduledDate = session.order.scheduledDate
        ? DateTime.fromISO(session.order.scheduledDate as string)
        : undefined;

    session.order.expirationDate = session.order.expirationDate
        ? DateTime.fromISO(session.order.expirationDate as string)
        : undefined;

    session.order.mentorActiveSince = session.order.mentorActiveSince
        ? DateTime.fromISO(session.order.mentorActiveSince as string)
        : undefined;

    //labels only used on mentor side now.  So can try theirs first.
    session.order.customer.labels.forEach(
        (i) =>
        (i.currentLanguage =
            i.languages[observerCultureCode!] ||
            i.languages[sessionCultureCode] ||
            i.languages[backupCultureCode] ||
            {})
    );

    //We want the tags themselves to always apply in language of Session. We could display to mentor in list in theirs
    //and apply to image in sessions
    session.order.tags.forEach(
        (i) => (i.currentLanguage = i.languages[sessionCultureCode] || i.languages[backupCultureCode] || {})
    );
    var product = session.order.product;
    product.currentLanguage =
        product.languages[observerCultureCode] ||
        product.languages[sessionCultureCode] ||
        product.languages[backupCultureCode] ||
        {};

    session.sessionInfo = hydrateSessionInfo(session.sessionInfo);

    session.localizations = session.localizations || {
        id: session.order.id,
        localizations: {},
    };

    session.sessionInfo.recordingToggles = session.sessionInfo.recordingToggles || [];

    return session;
}

function hydrateSessionInfo(sessionInfo: SessionInfo): SessionInfo {
    const info = sessionInfo ||
    {
        id: null,
        customerId: null,
        recordingToggles: [],
        videos: [],
        chats: [],
        photos: [],
        registrations: [],
        externalObservers: []
    };

    info.chats = sessionInfo.chats || [];
    info.photos = sessionInfo.photos || [];
    info.videos = sessionInfo.videos || [];
    info.registrations = sessionInfo.registrations || [];
    info.externalObservers = sessionInfo.externalObservers || [];
    info.photos.forEach((p) => (p.tags = p.tags || []));
    info.videos.forEach(
        (v) => (v.deletedDate = v.deletedDate !== null ? DateTime.fromISO(v.deletedDate as any) : (null as any))
    );

    return info;
}
